import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO, isBefore, startOfDay } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Car, Loader2, Trash2, Sun, Moon, Calendar as CalendarIcon, Settings, ShoppingCart } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PARKING_SPOT_IDS = ['94', '95', '84', '41', '42', '43', '10A', '10B'];

export default function ParkingPage() {
  const { isAdmin } = useAuth();
  const { t, dateLocale } = useLanguage();
  const [selectedDate, setSelectedDate] = useState(null);
  const [parkingSpots, setParkingSpots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('full_day');
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [holidays, setHolidays] = useState([]);

  const fetchParkingSpots = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/parking/spots`, { withCredentials: true });
      setParkingSpots(data.spots);
    } catch {
      toast.error(t('error'));
    }
  }, []);

  const fetchBookingsForDate = useCallback(async (date) => {
    if (!date) return;
    setLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const { data } = await axios.get(
        `${API_URL}/api/parking/bookings/by-date?date=${dateStr}`,
        { withCredentials: true }
      );
      setBookings(data);
    } catch {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyBookings = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/parking/bookings`,
        { withCredentials: true }
      );
      setMyBookings(data);
    } catch {
      console.error('Error fetching my bookings');
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchParkingSpots(),
        fetchMyBookings(),
        axios.get(`${API_URL}/api/holidays`, { withCredentials: true })
          .then(({ data }) => setHolidays(data.holidays || []))
          .catch(() => {})
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchParkingSpots, fetchMyBookings]);

  useEffect(() => {
    if (selectedDate) {
      fetchBookingsForDate(selectedDate);
      setSelectedSpot(null);
    }
  }, [selectedDate, fetchBookingsForDate]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const isSpotAvailable = (spotId) => {
    const spot = parkingSpots.find(s => s.spot_id === spotId);
    return spot ? spot.is_available : true;
  };

  const getSpotStatus = (spotId) => {
    if (!isSpotAvailable(spotId)) {
      return { status: 'disabled', bookings: [] };
    }
    
    const spotBookings = bookings.filter(b => b.spot_id === spotId);
    
    if (spotBookings.length === 0) return { status: 'free', bookings: [] };
    
    const hasMorning = spotBookings.some(b => b.period === 'morning' || b.period === 'full_day');
    const hasAfternoon = spotBookings.some(b => b.period === 'afternoon' || b.period === 'full_day');
    const hasFullDay = spotBookings.some(b => b.period === 'full_day');
    const isMine = spotBookings.some(b => b.is_mine);
    
    if (hasFullDay || (hasMorning && hasAfternoon)) {
      return { status: isMine ? 'mine' : 'occupied', bookings: spotBookings };
    }
    
    return { status: 'partial', bookings: spotBookings, hasMorning, hasAfternoon, isMine };
  };

  const canBookPeriod = (spotId, period) => {
    if (!isSpotAvailable(spotId)) return false;
    
    const { status, hasMorning, hasAfternoon } = getSpotStatus(spotId);
    
    if (status === 'free') return true;
    if (status === 'occupied' || status === 'mine' || status === 'disabled') return false;
    
    if (period === 'full_day') return false;
    if (period === 'morning' && hasMorning) return false;
    if (period === 'afternoon' && hasAfternoon) return false;
    
    return true;
  };

  const handleSpotClick = (spotId) => {
    if (!selectedDate) {
      toast.error(t('selectDateFirst'));
      return;
    }
    
    const { status } = getSpotStatus(spotId);
    if (status === 'occupied' || status === 'disabled') return;
    
    setSelectedSpot(spotId === selectedSpot ? null : spotId);
  };

  const handleBooking = async () => {
    if (!selectedSpot || !selectedDate) {
      toast.error(t('selectDateAndSpot'));
      return;
    }

    if (!canBookPeriod(selectedSpot, selectedPeriod)) {
      toast.error(t('periodNotAvailable'));
      return;
    }

    setBookingLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      
      if (isAdmin) {
        // Admin: direct booking
        await axios.post(`${API_URL}/api/parking/bookings`, {
          spot_id: selectedSpot, date: dateStr, period: selectedPeriod
        }, { withCredentials: true });
        const periodLabel = selectedPeriod === 'morning' ? t('morning') : 
                           selectedPeriod === 'afternoon' ? t('afternoon') : t('fullDayParking');
        toast.success(t('parkingBooked', { spot: selectedSpot, period: periodLabel }));
        await fetchBookingsForDate(selectedDate);
        await fetchMyBookings();
      } else {
        // User: add to cart
        await axios.post(`${API_URL}/api/cart/add`, {
          item_type: 'parking',
          resource_id: selectedSpot,
          dates: [dateStr],
          period: selectedPeriod
        }, { withCredentials: true });
        toast.success(t('addedToCart'));
      }
      setSelectedSpot(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('error'));
    } finally {
      setBookingLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await axios.delete(
        `${API_URL}/api/parking/bookings/${bookingId}`,
        { withCredentials: true }
      );
      toast.success(t('bookingCancelled'));
      if (selectedDate) {
        await fetchBookingsForDate(selectedDate);
      }
      await fetchMyBookings();
    } catch {
      toast.error(t('error'));
    }
  };

  const handleToggleSpotAvailability = async (spotId, isAvailable) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/parking/spots/${spotId}`,
        { is_available: isAvailable },
        { withCredentials: true }
      );
      await fetchParkingSpots();
      toast.success(isAvailable ? t('parkingActivated', { spot: spotId }) : t('parkingDeactivated', { spot: spotId }));
    } catch {
      toast.error(t('error'));
    }
  };

  const getSpotClasses = (spotId) => {
    const { status } = getSpotStatus(spotId);
    const isSelected = selectedSpot === spotId;
    
    let classes = 'w-12 h-10 md:w-16 md:h-12 rounded-lg flex flex-col items-center justify-center text-xs md:text-sm font-bold transition-all cursor-pointer border-2 ';
    
    if (status === 'disabled') {
      classes += 'bg-slate-200 text-slate-400 border-slate-300 cursor-not-allowed opacity-60 ';
    } else if (isSelected) {
      classes += 'bg-indigo-500 text-white border-indigo-300 ring-2 ring-indigo-300 scale-110 ';
    } else if (status === 'free') {
      classes += 'bg-emerald-100 text-emerald-700 border-emerald-300 hover:bg-emerald-200 hover:scale-105 ';
    } else if (status === 'mine') {
      classes += 'bg-blue-100 text-blue-700 border-blue-300 ';
    } else if (status === 'occupied') {
      classes += 'bg-rose-100 text-rose-700 border-rose-300 cursor-not-allowed ';
    } else if (status === 'partial') {
      classes += 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200 ';
    }
    
    return classes;
  };

  const today = startOfDay(new Date());

  const renderSpot = (spotId) => {
    return (
      <div
        key={spotId}
        data-testid={`parking-spot-${spotId}`}
        className={getSpotClasses(spotId)}
        onClick={() => handleSpotClick(spotId)}
      >
        <Car className="w-3 h-3 md:w-4 md:h-4" />
        <span className="text-[10px] md:text-xs">{spotId}</span>
      </div>
    );
  };

  const getPeriodLabel = (period) => {
    if (period === 'morning') return t('morning');
    if (period === 'afternoon') return t('afternoon');
    return t('parkingFullDay');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="parking-page">
      <Header />
      
      <main className="max-w-7xl mx-auto px-2 md:px-4 py-4 md:py-8">
        <div className="mb-4 md:mb-6">
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">{t('parkingTitle')}</h1>
          <p className="text-slate-500 mt-1 text-sm md:text-base">{t('parkingSubtitle')}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
          {/* Calendar */}
          <Card className="card-floating">
            <CardHeader className="pb-2 md:pb-4">
              <CardTitle className="text-lg md:text-xl font-extrabold text-slate-900">
                {t('selectDate')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 md:p-6 pt-0">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                locale={dateLocale}
                disabled={[{ before: today }, { dayOfWeek: [0, 6] }, ...holidays.map(h => new Date(h + 'T00:00:00'))]}
                modifiers={{ holiday: holidays.map(h => new Date(h + 'T00:00:00')) }}
                modifiersClassNames={{ holiday: 'rdp-day_disabled' }}
                className="rounded-xl"
                data-testid="parking-calendar"
              />
              
              {selectedDate && (
                <div className="mt-4 p-3 bg-blue-50 rounded-xl">
                  <p className="text-sm font-semibold text-blue-700">
                    {format(selectedDate, 'EEEE d MMMM yyyy', { locale: dateLocale })}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Parking Map */}
          <Card className="card-floating lg:col-span-2">
            <CardHeader className="pb-2 md:pb-4">
              <CardTitle className="text-lg md:text-xl font-extrabold text-slate-900">
                {t('parkingMap')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 md:p-6 pt-0">
              {/* Legend */}
              <div className="flex flex-wrap gap-2 md:gap-4 justify-center text-xs md:text-sm mb-4 md:mb-6">
                <div className="flex items-center gap-1 md:gap-2">
                  <div className="w-3 h-3 md:w-4 md:h-4 rounded bg-emerald-100 border border-emerald-300" />
                  <span className="text-slate-600">{t('parkingFree')}</span>
                </div>
                <div className="flex items-center gap-1 md:gap-2">
                  <div className="w-3 h-3 md:w-4 md:h-4 rounded bg-amber-100 border border-amber-300" />
                  <span className="text-slate-600">{t('parkingPartial')}</span>
                </div>
                <div className="flex items-center gap-1 md:gap-2">
                  <div className="w-3 h-3 md:w-4 md:h-4 rounded bg-rose-100 border border-rose-300" />
                  <span className="text-slate-600">{t('parkingOccupied')}</span>
                </div>
                <div className="flex items-center gap-1 md:gap-2">
                  <div className="w-3 h-3 md:w-4 md:h-4 rounded bg-blue-100 border border-blue-300" />
                  <span className="text-slate-600">{t('parkingYours')}</span>
                </div>
                <div className="flex items-center gap-1 md:gap-2">
                  <div className="w-3 h-3 md:w-4 md:h-4 rounded bg-slate-200 border border-slate-300" />
                  <span className="text-slate-600">{t('parkingNotActive')}</span>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center h-48 md:h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : (
                <div className="bg-slate-100 rounded-2xl md:rounded-3xl p-4 md:p-6 relative min-h-[280px] md:min-h-[400px]">
                  {/* Zone A */}
                  <div className="absolute top-4 md:top-6 left-4 md:left-6">
                    <p className="text-[10px] md:text-xs font-bold text-slate-500 mb-1 md:mb-2">Zona A</p>
                    <div className="flex gap-1 md:gap-2">
                      {renderSpot('94')}
                      {renderSpot('95')}
                    </div>
                  </div>

                  {/* Zone B */}
                  <div className="absolute top-1/4 left-1/4 md:left-1/3">
                    <p className="text-[10px] md:text-xs font-bold text-slate-500 mb-1 md:mb-2">Zona B</p>
                    <div className="flex flex-col gap-1 md:gap-2">
                      {renderSpot('84')}
                      <div className="flex gap-1 md:gap-2">
                        {renderSpot('41')}
                        {renderSpot('42')}
                        {renderSpot('43')}
                      </div>
                    </div>
                  </div>

                  {/* Zone C */}
                  <div className="absolute bottom-4 md:bottom-6 right-4 md:right-6">
                    <p className="text-[10px] md:text-xs font-bold text-slate-500 mb-1 md:mb-2">Zona C</p>
                    <div className="flex flex-col gap-1 md:gap-2">
                      {renderSpot('10A')}
                      {renderSpot('10B')}
                    </div>
                  </div>

                  {/* Area indicator */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-slate-300 text-[10px] md:text-xs font-medium">
                    {t('commonArea')}
                  </div>
                </div>
              )}

              {/* Booking panel */}
              {selectedSpot && selectedDate && (
                <div className="mt-4 md:mt-6 p-3 md:p-4 bg-indigo-50 rounded-xl md:rounded-2xl animate-fade-in-up">
                  <p className="font-bold text-indigo-900 mb-2 md:mb-3 text-sm md:text-base">
                    {t('parkingSpotLabel')} {selectedSpot} - {format(selectedDate, 'dd/MM/yyyy')}
                  </p>
                  
                  <div className="mb-3 md:mb-4">
                    <p className="text-xs md:text-sm text-indigo-700 mb-2">{t('selectPeriodParking')}</p>
                    <RadioGroup 
                      value={selectedPeriod} 
                      onValueChange={setSelectedPeriod}
                      className="flex flex-wrap gap-2 md:gap-4"
                    >
                      <div className="flex items-center space-x-1 md:space-x-2">
                        <RadioGroupItem 
                          value="morning" 
                          id="morning" 
                          disabled={!canBookPeriod(selectedSpot, 'morning')}
                          data-testid="period-morning"
                        />
                        <Label htmlFor="morning" className="flex items-center gap-1 cursor-pointer text-xs md:text-sm">
                          <Sun className="w-3 h-3 md:w-4 md:h-4 text-yellow-500" />
                          {t('morning')}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-1 md:space-x-2">
                        <RadioGroupItem 
                          value="afternoon" 
                          id="afternoon"
                          disabled={!canBookPeriod(selectedSpot, 'afternoon')}
                          data-testid="period-afternoon"
                        />
                        <Label htmlFor="afternoon" className="flex items-center gap-1 cursor-pointer text-xs md:text-sm">
                          <Moon className="w-3 h-3 md:w-4 md:h-4 text-indigo-500" />
                          {t('afternoon')}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-1 md:space-x-2">
                        <RadioGroupItem 
                          value="full_day" 
                          id="full_day"
                          disabled={!canBookPeriod(selectedSpot, 'full_day')}
                          data-testid="period-full-day"
                        />
                        <Label htmlFor="full_day" className="flex items-center gap-1 cursor-pointer text-xs md:text-sm">
                          <CalendarIcon className="w-3 h-3 md:w-4 md:h-4 text-emerald-500" />
                          {t('fullDayParking')}
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                  
                  <Button
                    onClick={handleBooking}
                    disabled={bookingLoading || !canBookPeriod(selectedSpot, selectedPeriod)}
                    data-testid="confirm-parking-btn"
                    className={`w-full sm:w-auto text-sm ${isAdmin ? 'btn-primary' : 'bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6 font-bold'}`}
                  >
                    {bookingLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isAdmin ? (
                      t('confirm')
                    ) : (
                      <><ShoppingCart className="w-4 h-4 mr-1" />{t('addToCart')}</>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Admin: Manage Parking */}
        {isAdmin && (
          <Card className="card-floating mt-4 md:mt-6">
            <CardHeader className="pb-2 md:pb-4">
              <CardTitle className="text-lg md:text-xl font-extrabold text-slate-900 flex items-center gap-2">
                <Settings className="w-4 h-4 md:w-5 md:h-5" />
                {t('manageParking')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-500 mb-4">
                {t('toggleParkingAvailability')}
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {parkingSpots.map((spot) => (
                  <div
                    key={spot.spot_id}
                    data-testid={`parking-toggle-${spot.spot_id}`}
                    className={`rounded-2xl p-4 flex items-center justify-between ${
                      spot.is_available 
                        ? 'bg-emerald-50 border-2 border-emerald-200' 
                        : 'bg-slate-100 border-2 border-slate-200'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Car className="w-5 h-5" />
                      <div>
                        <p className="font-bold text-slate-900">{t('spot')} {spot.spot_id}</p>
                        <p className={`text-sm ${spot.is_available ? 'text-emerald-600' : 'text-slate-400'}`}>
                          {spot.is_available ? t('active') : t('disabled')}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={spot.is_available}
                      onCheckedChange={(checked) => handleToggleSpotAvailability(spot.spot_id, checked)}
                      data-testid={`parking-switch-${spot.spot_id}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* My Bookings */}
        <Card className="card-floating mt-6">
          <CardHeader>
            <CardTitle className="text-xl font-extrabold text-slate-900">
              {t('yourParkingBookings')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {myBookings.length === 0 ? (
              <p className="text-slate-500 text-center py-8">
                {t('noParkingBookings')}
              </p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {myBookings
                  .sort((a, b) => a.date.localeCompare(b.date))
                  .map((booking) => {
                    const bookingDate = parseISO(booking.date);
                    const isPast = isBefore(bookingDate, today);
                    
                    return (
                      <div
                        key={booking.id}
                        data-testid={`my-parking-${booking.id}`}
                        className={`rounded-2xl p-4 flex justify-between items-center ${
                          isPast ? 'bg-slate-100' : 'bg-gradient-to-br from-emerald-50 to-teal-50'
                        }`}
                      >
                        <div>
                          <p className={`font-bold ${isPast ? 'text-slate-500' : 'text-slate-900'}`}>
                            <Car className="w-4 h-4 inline mr-1" />
                            {t('parkingSpotLabel')} {booking.spot_id}
                          </p>
                          <p className={`text-sm ${isPast ? 'text-slate-400' : 'text-slate-600'}`}>
                            {format(bookingDate, 'EEEE d MMMM', { locale: dateLocale })}
                          </p>
                          <p className={`text-xs ${isPast ? 'text-slate-400' : 'text-teal-600'}`}>
                            {getPeriodLabel(booking.period)}
                          </p>
                        </div>
                        {!isPast && (
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`cancel-parking-${booking.id}`}
                            onClick={() => handleCancelBooking(booking.id)}
                            className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-full"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
